import { useEffect, useId, useRef } from 'react';
import { useToastStore } from '@/store/useToastStore';

/**
 * Mirrors a screen's error state into the error popup.
 *
 * `error` is the screen's own state — nothing about how it is set changes.
 * When it becomes a message the popup opens; when the screen clears it (the
 * retry succeeded, the input was fixed) or the screen closes, the popup goes
 * with it. A new message replaces this screen's previous popup.
 *
 * `clear` is the screen's own reset (`() => setError(null)`), run when the
 * operator closes the popup — so the same error raised again shows again.
 */
export function useErrorPopup(error: string | null | undefined | false, clear?: () => void): void {
  const source = useId();
  const push = useToastStore((s) => s.push);
  const dismissSource = useToastStore((s) => s.dismissSource);
  const clearRef = useRef(clear);
  clearRef.current = clear;
  useEffect(() => {
    if (error) push(error, { tone: 'error', source, onClose: () => clearRef.current?.() });
    else dismissSource(source);
  }, [error, source, push, dismissSource]);
  useEffect(() => () => dismissSource(source), [source, dismissSource]);
}
